import { z } from 'zod';
/** Dashboard demo-data slice — the tables the reference dashboard view-model reads (design-reference script lines
 *  4148–4173 SETS, 4459–4465 G, 4537–4544 SESS/HOUR_W/HOUR_L, 4693–4700 ALL). Deterministic, seeded, labeled simulated. */

export const DashboardRangeIdSchema = z.enum(['day', 'week', 'month', 'year', 'all']);
/** One equity P&L bar: [label, P&L, % change] */
export const EqPointSchema = z.tuple([z.string(), z.number(), z.number()]);
/** Gauge-card period row (G table). */
export const GaugePeriodSchema = z
  .object({
    label: z.string(),
    closed: z.number(),
    wins: z.number(),
    open: z.number(),
    daysTraded: z.number(),
    daysProfit: z.number(),
    roi: z.number(),
    pnl: z.number(),
    scale: z.number(),
  })
  .strict();
/** Multi-account equity curve account definition (ALL). */
export const McAccountSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    col: z.string(),
    start: z.number(),
    drift: z.number(),
    vol: z.number(),
    seed: z.number(),
  })
  .strict();
/** Trading session: [name, share of period P&L, win rate %] */
export const SessionRowSchema = z.tuple([z.string(), z.number(), z.number()]);
const perRange = <T extends z.ZodTypeAny>(s: T) =>
  z.object({ day: s, week: s, month: s, year: s, all: s }).strict();
export const DashboardDataSchema = z
  .object({
    /** EQ_NOW — account equity as it stands now, in whole currency units.
     *
     *  A number rather than the formatted header triple it used to be. The card's P&L and percentage are the
     *  *selected range's* movement, so they change when the trader switches Day/Week/Month, and a stored triple
     *  could only ever be right for one of them — it read "+$42,692.00 · +8.54%" (the year) under every range. */
    equity: z.number(),
    /** SETS — equity P&L series per range. */
    eqSets: perRange(z.array(EqPointSchema)),
    /** G — gauge table per range. */
    gauges: perRange(GaugePeriodSchema),
    /** ALL — multi-account equity curve definitions. */
    accounts: z.array(McAccountSchema),
    /** SESS — session P&L shares and win rates. */
    sessions: z.array(SessionRowSchema),
    /** HOUR_W / HOUR_L — entry-hour weights and labels. */
    hourWeights: z.array(z.number()),
    hourLabels: z.array(z.string()),
    /** Realised P&L per calendar day, keyed `YYYY-MM-DD`. The P&L calendar's only source.
     *
     *  It exists because the calendar used to GENERATE its figures: a pseudo-random series seeded from the month
     *  alone, reading no ledger, so every trader saw the same invented wins and losses and a brand-new account saw
     *  a full month of them. That is a design reference's behaviour — the reference is a mock and its calendar is
     *  decoration — inherited by a view-model that should have been reading the trader's own days.
     *
     *  Keeping it in the dataset rather than in the view-model is the directive's rule that no component holds
     *  hardcoded display data, and it is what lets the two bindings differ honestly: the simulator supplies the
     *  reference's own values so the visual suite still matches pixel-for-pixel, while the live binding supplies
     *  the `day` aggregate. A day the trader did not trade is absent, not zero. */
    calendar: z.record(z.string(), z.number()),
  })
  .strict();
export type DashboardData = z.infer<typeof DashboardDataSchema>;
export type DashboardRangeId = z.infer<typeof DashboardRangeIdSchema>;
export type EqPoint = z.infer<typeof EqPointSchema>;
export type GaugePeriod = z.infer<typeof GaugePeriodSchema>;
export type McAccount = z.infer<typeof McAccountSchema>;
